import { Badge, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { revokeMonitoringLink } from '../../api/resources';
import { LIFECYCLE_COLORS, formatDateTime, progressStageLabel } from '../../lib/monitoringLinks';
import type { MonitoringLinkLifecycle, MonitoringLinkListItem } from '../../types/api';

export function LifecycleBadge({ lifecycle }: { lifecycle: MonitoringLinkLifecycle }) {
    const { t } = useTranslation();
    return (
        <Badge variant="light" color={LIFECYCLE_COLORS[lifecycle]}>
            {t(`features.monitoringLinks.lifecycle.${lifecycle}`)}
        </Badge>
    );
}

/** Where the customer got to: their latest step, when, and how many steps so far. */
export function ProgressSummary({ link }: { link: MonitoringLinkListItem }) {
    const { t } = useTranslation();
    const progress = link.progress;
    const lastActive = formatDateTime(progress?.lastActiveAt);
    const meta = [
        lastActive ? t('features.monitoringLinks.lastActive', { when: lastActive }) : null,
        progress?.eventCount
            ? t('features.monitoringLinks.stepCount', { count: progress.eventCount })
            : null,
    ].filter(Boolean);

    return (
        <Stack gap={0}>
            <Text size="sm" fw={600}>
                {progressStageLabel(t, progress?.stage)}
            </Text>
            {meta.length > 0 ? (
                <Text size="xs" c="dimmed">
                    {meta.join(' · ')}
                </Text>
            ) : null}
        </Stack>
    );
}

/** Confirms, then revokes. Only live links can be revoked. */
export function RevokeMonitoringLinkModal({
    link,
    onClose,
}: {
    link: MonitoringLinkListItem | null;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const revokeMutation = useMutation({
        mutationFn: (linkId: string) => revokeMonitoringLink(linkId),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.monitoringLinks.revoked') });
            void queryClient.invalidateQueries({ queryKey: ['monitoring-links'] });
            onClose();
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    return (
        <Modal
            opened={link !== null}
            onClose={onClose}
            title={t('features.monitoringLinks.revokeTitle')}
        >
            <Stack gap="md">
                <Text size="sm">
                    {t('features.monitoringLinks.revokeConfirm', {
                        name: link?.subjectName ?? t('entities.subject'),
                    })}
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose}>
                        {t('common.actions.cancel')}
                    </Button>
                    <Button
                        color="red"
                        loading={revokeMutation.isPending}
                        onClick={() => link && revokeMutation.mutate(link.id)}
                    >
                        {t('features.monitoringLinks.revokeAction')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
